import {
  DocumentTypeAttributes,
  DocumentTypeBlockBlockType,
  DocumentTypeOrientation,
  DocumentTypeSize,
  DocumentTypeBlockMarginsAttributes,
  Color,
  DocumentSubjectType
} from '@/types'
import { UpdateDocumentTypeMutation } from './__generated__/update_mutation'
import { Mutation as UpdateMutation } from './update_mutation'
import { useForm } from '@/helpers/useForm'
import { EditDocumentTypeQuery } from './__generated__/edit_query'
import { Query as EditQuery } from './edit_query'
import { Inline as Loading } from '@/components/loading'
import { Inline as Error } from '@/components/error'
import { Selector as MarginSelector } from './margin/selector'
import { Selector as ColorSelector } from './color/selector'
import { Field, Label } from '../base/fieldset'
import { ChevronUpDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/solid'
import { Button } from '../base/button'
import { Input } from '../base/input'
import { useState } from 'react'

type DocumentType = EditDocumentTypeQuery['documentTypes']['nodes'][0]

interface Props {
  documentTypeId: string
}

const friendlyBlockNames: Record<string, string> = {
  [DocumentTypeBlockBlockType.Address]: 'Address',
  [DocumentTypeBlockBlockType.AssetList]: 'Asset list',
  [DocumentTypeBlockBlockType.AssetPackList]: 'Asset pack list',
  [DocumentTypeBlockBlockType.AssetTypePackList]: 'Asset type pack list',
  [DocumentTypeBlockBlockType.DocumentDetails]: 'Document details',
  [DocumentTypeBlockBlockType.EventDetails]: 'Event details',
  [DocumentTypeBlockBlockType.EventItemsTable]: 'Event items table',
  [DocumentTypeBlockBlockType.EventPatDetails]: 'Event PAT details',
  [DocumentTypeBlockBlockType.EventQr]: 'Event QR',
  [DocumentTypeBlockBlockType.Html]: 'HTML',
  [DocumentTypeBlockBlockType.Logo]: 'Logo',
  [DocumentTypeBlockBlockType.MaintenanceHistory]: 'Maintenance history',
  [DocumentTypeBlockBlockType.MaintenanceSchedules]: 'Maintenance schedules',
  [DocumentTypeBlockBlockType.SpecificationsTable]: 'Specifications table',
  [DocumentTypeBlockBlockType.Subtitle]: 'Subtitle',
  [DocumentTypeBlockBlockType.Text]: 'Text',
  [DocumentTypeBlockBlockType.Title]: 'Title',
}

const commonBlocks = [
  DocumentTypeBlockBlockType.Title,
  DocumentTypeBlockBlockType.Subtitle,
  DocumentTypeBlockBlockType.Text,
  DocumentTypeBlockBlockType.DocumentDetails,
  DocumentTypeBlockBlockType.Address,
  DocumentTypeBlockBlockType.Logo,
  DocumentTypeBlockBlockType.Html,
]

const blockGroups: { [key in DocumentSubjectType]: DocumentTypeBlockBlockType[] } = {
  [DocumentSubjectType.Event]: [
    DocumentTypeBlockBlockType.EventDetails,
    DocumentTypeBlockBlockType.EventItemsTable,
    DocumentTypeBlockBlockType.AssetTypePackList,
    DocumentTypeBlockBlockType.AssetPackList,
    DocumentTypeBlockBlockType.EventPatDetails,
    DocumentTypeBlockBlockType.EventQr,
  ],
  [DocumentSubjectType.Asset]: [
    DocumentTypeBlockBlockType.MaintenanceHistory,
    DocumentTypeBlockBlockType.MaintenanceSchedules,
  ],
  [DocumentSubjectType.AssetType]: [
    DocumentTypeBlockBlockType.SpecificationsTable,
    DocumentTypeBlockBlockType.AssetList,
  ],
  [DocumentSubjectType.Package]: [],
  [DocumentSubjectType.Venue]: [],
}

const templateableBlocks = [
  DocumentTypeBlockBlockType.Title,
  DocumentTypeBlockBlockType.Subtitle,
  DocumentTypeBlockBlockType.Text,
  DocumentTypeBlockBlockType.Html,
]

export const EditDesign = ({ documentTypeId }: Props) => {
  const {
    save,
    saveSuccess,
    formBuilder,
    queryError,
    queryLoading,
    mutationLoading,
    attributes,
    setAttributes,
  } = useForm<UpdateDocumentTypeMutation, DocumentTypeAttributes, EditDocumentTypeQuery, DocumentType>({
    id: documentTypeId,
    initializer: documentType => ({
      name: documentType.name,
      subject: documentType.subject,
      incremental: documentType.incremental,
      incrementStart: documentType.incrementStart,
      dateDue: documentType.dateDue,
      dateIssued: documentType.dateIssued,
      size: documentType.size || DocumentTypeSize.A4,
      orientation: documentType.orientation || DocumentTypeOrientation.Portrait,
      blocks: documentType.documentTypeBlocks?.map(block => ({
        blockType: block.blockType,
        margins: {
          top: block.margins.top || 0,
          bottom: block.margins.bottom || 0,
          left: block.margins.left || 0,
          right: block.margins.right || 0
        },
        options: {
          color: block.options.color
        },
        template: block.template
      })) || [],
    }),
    query: EditQuery,
    queryKey: 'documentTypes',
    mutation: UpdateMutation,
    resultKey: 'updateDocumentType'
  })
  const [dragOrigin, setDragOrigin] = useState<number | undefined>()
  const [dragDestination, setDragDestination] = useState<number | undefined>()

  const setMargin = (blockIndex: number, margin: keyof DocumentTypeBlockMarginsAttributes, value: number) => {
    setAttributes(v => {
      const newBlocks = [...v.blocks || []]
      const newBlock = {
        ...newBlocks[blockIndex],
        margins: {
          top: newBlocks[blockIndex].margins?.top,
          bottom: newBlocks[blockIndex].margins?.bottom,
          left: newBlocks[blockIndex].margins?.left,
          right: newBlocks[blockIndex].margins?.right
        },
      }
      newBlock.margins[margin] = value
      newBlocks[blockIndex] = newBlock
      return {
        ...v,
        blocks: newBlocks
      }
    })
  }

  const setColor = (blockIndex: number, value: Color) => {
    setAttributes(v => {
      const newBlocks = [...v.blocks || []]
      const newBlock = {
        ...newBlocks[blockIndex],
        options: { color: value },
      }
      newBlocks[blockIndex] = newBlock
      return {
        ...v,
        blocks: newBlocks
      }
    })
  }

  const setTemplate = (blockIndex: number, value: string) => {
    setAttributes(v => {
      const newBlocks = [...v.blocks || []]
      const newBlock = {
        ...newBlocks[blockIndex],
        template: value,
      }
      newBlocks[blockIndex] = newBlock
      return {
        ...v,
        blocks: newBlocks
      }
    })
  }

  const addBlock = (blockType: DocumentTypeBlockBlockType) => {
    setAttributes(v => {
      const newBlocks = [...v.blocks || []]
      newBlocks.push({
        blockType,
        margins: { top: 0, bottom: 0, left: 0, right: 0 },
        options: { color: Color.Black },
        template: ''
      })
      return {
        ...v,
        blocks: newBlocks
      }
    })
  }

  const removeBlock = (blockIndex: number) => {
    setAttributes(v => {
      const newBlocks = [...v.blocks || []]
      newBlocks.splice(blockIndex, 1)
      return {
        ...v,
        blocks: newBlocks
      }
    })
  }

  const onDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    setDragOrigin(parseInt(event.currentTarget.dataset.position || ''))
  }

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragDestination(parseInt(event.currentTarget.dataset.position || ''))
  }

  const onDrop = () => {
    if (dragOrigin == undefined || dragDestination == undefined) return

    setAttributes(v => {
      const blocks = v.blocks || []
      const oldBlock = blocks.splice(dragOrigin, 1)[0]
      const newBlocks = [
        ...blocks.slice(0, dragDestination),
        oldBlock,
        ...blocks.slice(dragDestination),
      ]
      return {
        ...v,
        blocks: newBlocks
      }
    })

    setDragOrigin(undefined)
    setDragDestination(undefined)
  }

  if (queryLoading) return <Loading />

  return (
    <>
      { queryError && <Error message={queryError} /> }
      <div className="flex flex-col gap-6">
        { formBuilder.buildQueryError() }
        <div className="flex flex-col divide-y divide-white/10">
          <div className="flex flex-row gap-4 pb-4 flex-wrap">
            { [...commonBlocks, ...blockGroups[attributes.subject || DocumentSubjectType.AssetType]].map(block => (
              <Button outline onClick={() => addBlock(block)}>
                <PlusIcon />{ friendlyBlockNames[block] }
              </Button>
            )) }
          </div>
          { attributes.blocks?.map((block, index) => (
            <div
              data-position={index}
              className={`
                flex flex-row w-full gap-6 transition
                ${dragOrigin == index ? 'scale-95' : ''}
                ${dragDestination == index ? 'bg-white/10' : ''}
              `}
              draggable
              onDragOver={onDragOver}
              onDragStart={onDragStart}
              onDrop={onDrop}
            >
              <div key={index} className="grow grid grid-cols-8 gap-6 py-3 items-center">
                <p className="col-span-2">{ friendlyBlockNames[block.blockType] }</p>
                <Field>
                  <Label>Text color</Label>
                  <ColorSelector value={block.options?.color || Color.Black} onChange={v => setColor(index, v)} />
                </Field>
                <span />
                <Field>
                  <Label>Top</Label>
                  <MarginSelector value={block.margins?.top || 0} onChange={v => setMargin(index, 'top', v)} />
                </Field>
                <Field>
                  <Label>Bottom</Label>
                  <MarginSelector value={block.margins?.bottom || 0} onChange={v => setMargin(index, 'bottom', v)} />
                </Field>
                <Field>
                  <Label>Left</Label>
                  <MarginSelector value={block.margins?.left || 0} onChange={v => setMargin(index, 'left', v)} />
                </Field>
                <Field>
                  <Label>Right</Label>
                  <MarginSelector value={block.margins?.right || 0} onChange={v => setMargin(index, 'right', v)} />
                </Field>
                { templateableBlocks.includes(block.blockType) &&
                  <Field className="col-start-3 col-span-6">
                    <Label>Template</Label>
                    <Input
                      type="text"
                      value={block.template || ''}
                      onChange={v => setTemplate(index, v.target.value)}
                    />
                  </Field> }
              </div>
              <div className="py-3 flex flex-col gap-4 items-center justify-between">
                <ChevronUpDownIcon className="size-4 cursor-move" />
                <span>
                  <Button color="red" onClick={() => removeBlock(index)}><TrashIcon color="white" /></Button>
                </span>
              </div>
            </div>

          )) }
        </div>
        { formBuilder.saveButton(save, mutationLoading, saveSuccess) }
      </div>
    </>
  )
}
